import React, { CSSProperties, Fragment } from "react";

import MaterialTable, { MTableBodyRow as Row } from "material-table";

import {
  Card,
  CardHeader,
  Divider,
  Typography,
  makeStyles,
} from "@material-ui/core";

import Label from "@ds-developer/label";

import { DiscountType, DiscountValueType } from "@hubcapp/types";

import { AreaDiscountsProps } from "./types";
import {
  badgeColorForDiscountType,
  formatValue,
  labelForDiscountType,
} from "./helpers";
import Skeleton from "./components/Skeleton";

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3, 2),
  },
  row: {
    [theme.breakpoints.up("md")]: {
      height: "72px",
    },
  },
}));

const AreaDiscounts: React.FC<AreaDiscountsProps> = ({
  loading = false,
  onChange = () => {},
  data = [],
  title = "Opt–in to discounts",
}) => {
  const classes = useStyles();
  const noWrap: CSSProperties = {
    whiteSpace: "nowrap",
  };
  const noWrapDirectives = {
    headerStyle: noWrap,
    cellStyle: noWrap,
  };

  return (
    <Card data-test="area-discounts-card" className="mobile-friendly">
      <CardHeader title={title} />
      <Divider />
      {loading ? (
        <Skeleton />
      ) : (
        <Fragment>
          <Typography classes={{ root: classes.root }}>
            {Boolean(data?.length) && (
              <Fragment>
                Here are the discounts available. Toggle{" "}
                {data.length === 1 ? "it" : "them"} on or off whenever you want
                per area.
              </Fragment>
            )}
          </Typography>
          <MaterialTable
            onSelectionChange={onChange}
            columns={[
              {
                title: "Name",
                field: "discountName",
                sorting: false,
                ...noWrapDirectives,
                render: ({ discountName }) => (
                  <div data-label="Name">{discountName}</div>
                ),
              },
              {
                title: "Value",
                field: "value",
                ...noWrapDirectives,
                render: ({ value, valueType }) => (
                  <div data-label="Value">
                    {formatValue(value, valueType || DiscountValueType.Hourly)}
                  </div>
                ),
              },
              {
                title: "Type",
                field: "type",
                sorting: false,
                ...noWrapDirectives,
                render: ({ discountType }) => (
                  <div data-label="Type">
                    <Label
                      color={badgeColorForDiscountType(
                        discountType || DiscountType.NewLearner,
                      )}
                    >
                      {labelForDiscountType(
                        discountType || DiscountType.NewLearner,
                      )}
                    </Label>
                  </div>
                ),
              },
              {
                title: "Description",
                field: "description",
                sorting: false,
                ...noWrapDirectives,
                render: ({ description }) => (
                  <div data-label="Description">{description}</div>
                ),
              },
            ]}
            data={data}
            options={{
              sorting: true,
              paging: false,
              search: false,
              selection: true,
              toolbar: false,
            }}
            components={{
              Row: props => (
                <Row
                  className={classes.row}
                  data-test={`discounts-table-row-${props.index}`}
                  {...props}
                />
              ),
            }}
          />
        </Fragment>
      )}
    </Card>
  );
};

export default AreaDiscounts;
